import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import '../../pages/Pages.css';
import artfooter from '../home/image/artfooter.png';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { login, loading, error, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check for state from registration or account claiming
  useEffect(() => {
    if (location.state?.registered) {
      setSuccessMessage(location.state.message || t('account_created_login'));
    } else if (location.state?.accountClaimed) {
      setSuccessMessage(t('account_claimed_success'));
      // Pre-fill email if available
      if (location.state.email) {
        setFormData(prev => ({
          ...prev,
          email: location.state.email
        }));
      }
    } else if (location.state?.passwordReset) {
      setSuccessMessage(t('password_reset_success'));
    }
  }, [location.state, t]);

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/profile');
    }
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    
    const { email, password } = formData;
    
    // Form validation
    if (!email || !password) {
      setFormError(t('please_fill_all_fields'));
      return;
    }
    
    try {
      await login(email, password);
      navigate('/profile');
    } catch (err) {
      console.error('Login error:', err);
      setFormError(err.message || t('login_error'));
    }
  };

  // Don't render the form if already logged in
  if (currentUser) {
    return null;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="logo-container logo-ellipse">
          <img src={artfooter} alt={t('studio_name')} className="footer-logo-img" />
        </div>
        
        <p className="welcome-text">{t('welcome_back_studio')}</p>
        
        <h2>{t('login')}</h2>
        
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        
        {(formError || error) && (
          <div className="error-message">
            {formError || error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('enter_your_email')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('enter_your_password')}
              required
            />
            <div className="forgot-password-link">
              <Link to="/forgot-password">{t('forgot_password_link', 'Forgot password?')}</Link>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? t('logging_in') : t('login')}
          </button>
        </form>
        
        <p className="auth-link">
          {t('dont_have_account')} <Link to="/register">{t('register_here')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
